import React from 'react';
import { motion } from 'framer-motion';
import { 
  BuildingOfficeIcon,
  ChartBarIcon,
  CogIcon,
  GlobeEuropeAfricaIcon 
} from '@heroicons/react/24/outline';

const TeamSection = () => {
  const teamMembers = [
    {
      name: "Alexander Nordström",
      role: "CEO & Founder",
      image: "https://images.unsplash.com/photo-1652471943570-f3590a4e52ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxidXNpbmVzc21hbiUyMGhlYWRzaG90fGVufDB8fHx8MTc2MzEzNTE5Mnww&ixlib=rb-4.1.0&q=85",
      bio: "Former Goldman Sachs VP with 15+ years in European real estate markets. Led €2B+ in property acquisitions.",
      expertise: "Real Estate Investment",
      icon: BuildingOfficeIcon,
      gradient: "from-purple-500 to-violet-600"
    },
    {
      name: "Sofia Andersson",
      role: "CTO & Blockchain Lead",
      image: "https://images.unsplash.com/photo-1689600944138-da3b150d9cb8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxidXNpbmVzc3dvbWFuJTIwaGVhZHNob3R8ZW58MHx8fHwxNzYzMTM1MjAwfDA&ixlib=rb-4.1.0&q=85",
      bio: "Ex-ConsenSys blockchain architect. Pioneer in tokenized real estate solutions with 50+ successful projects.",
      expertise: "Blockchain Technology",
      icon: CogIcon,
      gradient: "from-cyan-500 to-blue-600"
    },
    {
      name: "Marcus Weber",
      role: "Head of Investments",
      image: "https://images.pexels.com/photos/2182970/pexels-photo-2182970.jpeg",
      bio: "20+ years managing premium European property portfolios. €5B+ assets under management across 12 countries.",
      expertise: "Portfolio Management",
      icon: ChartBarIcon,
      gradient: "from-green-500 to-emerald-600"
    }
  ];

  const achievements = [
    {
      metric: "€2.5B+",
      description: "Combined Portfolio Value Managed",
      color: "text-purple-400"
    },
    {
      metric: "50+",
      description: "Successful Property Projects",
      color: "text-cyan-400"
    },
    {
      metric: "15+",
      description: "Years Average Experience",
      color: "text-green-400"
    },
    {
      metric: "12",
      description: "European Countries Coverage",
      color: "text-yellow-400"
    }
  ];

  return (
    <section id="team" className="py-20 bg-gradient-to-b from-gray-800 to-gray-900 relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1704655295066-681e61ecca6b"
          alt="Professional Team"
          className="w-full h-full object-cover opacity-5"
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
            Meet Our Team
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-4">
            We are a team of advanced, specially-trained AI agents. Think of us as your dedicated digital experts, designed with one purpose: to guide you to success.
          </p>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-4">
            We were created by analyzing decades of combined knowledge and experience. All the best practices, strategies, and data from European real estate, blockchain technology, and investment management have been programmed into how we think and operate. This means you don't just get one human expert; you get the collective intelligence of an entire field, working for you 24/7.
          </p>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            We're here to help you spot opportunities, understand risks, and make confident decisions. We are the next-generation tool, built on old-generation wisdom.
          </p>
        </motion.div>

        {/* Team Achievements */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          {achievements.map((achievement, index) => (
            <motion.div
              key={index}
              className="text-center bg-gray-800/30 backdrop-blur-lg rounded-2xl p-6 border border-gray-700"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05 }}
            >
              <div className={`text-4xl font-bold mb-2 ${achievement.color}`}>
                {achievement.metric}
              </div>
              <div className="text-gray-400 text-sm leading-tight">
                {achievement.description}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Team Members */}
        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          {teamMembers.map((member, index) => (
            <motion.div
              key={index}
              className="bg-gray-800/50 backdrop-blur-lg rounded-3xl p-6 border border-gray-700 hover:border-purple-500 transition-all duration-300"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -10, scale: 1.02 }}
            >
              {/* Profile Image */}
              <div className="relative mb-6">
                <motion.img
                  src={member.image}
                  alt={member.name}
                  className="w-32 h-32 rounded-full mx-auto object-cover border-4 border-gray-700"
                  whileHover={{ scale: 1.1 }}
                />
                <div className={`absolute -bottom-2 -right-2 w-12 h-12 bg-gradient-to-r ${member.gradient} rounded-full flex items-center justify-center`}>
                  <member.icon className="w-6 h-6 text-white" />
                </div>
              </div>

              {/* Member Info */}
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-2">{member.name}</h3>
                <div className={`text-lg font-semibold mb-3 bg-gradient-to-r ${member.gradient} bg-clip-text text-transparent`}>
                  {member.role}
                </div>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  {member.bio}
                </p>
                <div className="bg-gray-700/50 rounded-xl px-4 py-2">
                  <div className="text-xs font-semibold text-gray-300 mb-1">EXPERTISE</div>
                  <div className="text-sm text-white font-medium">{member.expertise}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default TeamSection;